/** Keep in sync with backend `app/services/purchase_pricing.py`. */

import { PLATFORM_COURIERS_ENABLED } from './platformShipping'

import type { ConditionRating } from './conditions'

export type { ConditionRating } from './conditions'
export {
  CONDITION_COLORS,
  CONDITION_RATINGS,
  conditionColor,
} from './conditions'

export type PurchaseRoute = 'direct' | 'guarantee'

export type ShipmentMethod = 'courier' | 'locker' | 'handover'

export const PLATFORM_FEE_RATE = 0

const LEG_SHIPPING_EUR: Record<ShipmentMethod, number> = {
  locker: 3.49,
  courier: 5.99,
  handover: 0,
}

export type PurchaseQuote = {
  route: PurchaseRoute
  devicePriceEur: number
  shippingEur: number
  inspectionFeeEur: number
  buyerTotalEur: number
  sellerReceivesEur: number
  platformFeeEur: number
  shippingLegs: number
}

export type QuoteListingInput = {
  price: number
  condition?: ConditionRating | string | null
  shipment_method?: ShipmentMethod | string | null
  shipping_included?: boolean
  shipping_cost_eur?: number | null
}

function legShippingEur(method: ShipmentMethod | string | null | undefined): number {
  if (!PLATFORM_COURIERS_ENABLED) return 0
  if (method === 'handover') return 0
  if (method === 'courier' || method === 'locker') return LEG_SHIPPING_EUR[method]
  return LEG_SHIPPING_EUR.locker
}

export function inspectionFeeEur(price: number, condition?: ConditionRating | string | null): number {
  let fee = 5
  if (price >= 600) fee = 9
  else if (price >= 300) fee = 7
  if (condition === 'damaged') fee = Math.max(fee, 9)
  else if (condition === 'fair') fee = Math.max(fee, 7)
  return fee
}

function sellerShippingDeduction(listing: QuoteListingInput): number {
  if (listing.shipping_included && listing.shipping_cost_eur) {
    return Math.round(Number(listing.shipping_cost_eur) * 100) / 100
  }
  return 0
}

export function quotePurchase(listing: QuoteListingInput, route: PurchaseRoute): PurchaseQuote {
  const device = Math.round(Number(listing.price) * 100) / 100
  const shipDeduction = sellerShippingDeduction(listing)
  const method = listing.shipment_method

  let shipping = 0
  let inspection = 0
  let legs = 0

  if (route === 'direct') {
    if (!listing.shipping_included && method !== 'handover') {
      shipping = legShippingEur(method)
      legs = shipping > 0 ? 1 : 0
    }
  } else {
    const leg = legShippingEur(method)
    shipping = PLATFORM_COURIERS_ENABLED ? Math.round(leg * 2 * 100) / 100 : 0
    inspection = inspectionFeeEur(device, listing.condition)
    legs = shipping > 0 ? 2 : 0
  }

  const buyerTotal = Math.round((device + shipping + inspection) * 100) / 100
  const sellerReceives = Math.round((device - shipDeduction) * 100) / 100
  const platformFee = Math.round((buyerTotal - sellerReceives) * 100) / 100

  return {
    route,
    devicePriceEur: device,
    shippingEur: shipping,
    inspectionFeeEur: inspection,
    buyerTotalEur: buyerTotal,
    sellerReceivesEur: Math.max(0, sellerReceives),
    platformFeeEur: Math.max(0, platformFee),
    shippingLegs: legs,
  }
}

export function calcSellerPayout(
  priceEur: number,
  shippingDeductionEur = 0,
): { fee: number; receives: number } {
  if (!Number.isFinite(priceEur) || priceEur <= 0) {
    return { fee: 0, receives: 0 }
  }
  const ship =
    Number.isFinite(shippingDeductionEur) && shippingDeductionEur > 0
      ? Math.round(shippingDeductionEur * 100) / 100
      : 0
  const receives = Math.round((priceEur - ship) * 100) / 100
  return { fee: 0, receives: Math.max(0, receives) }
}
